import { useQueryClient } from '@tanstack/react-query'
import { ArrowDownNarrowWideIcon, ArrowUpNarrowWideIcon, InfoIcon, LoaderCircleIcon, RefreshCcw, X } from 'lucide-react'
import React, { useState } from 'react'
import { toast } from 'sonner'

import { CollapsibleContainer } from 'slab'
import { cn } from 'utils'
import { type PayrollProviderConfig } from 'utils/web_api.schemas'
import { useWindowLocation } from 'utils/windowLocation'

import { getPayrollProviderConfigsQueryKey, useGetPayrollProviderConfigs } from '../hooks/useGetPayrollProviderConfigs'
import { useSyncPayrollProviderConfigs } from '../hooks/useSyncPayrollProviderConfigs'
import { useUpdatePayrollProviderConfig } from '../hooks/useUpdatePayrollProviderConfig'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Switch } from '../ui/switch'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip'

const AssistedBadge = ({ tooltipContent }: { tooltipContent: string }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger>
        <span className='text-xs text-yellow-600 bg-yellow-100 px-2 rounded-md cursor-default flex items-center gap-1'>
          Assisted
          <InfoIcon className='w-3 h-3 text-yellow-600' />
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <p className='max-w-xs'>{tooltipContent}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
)

const PayrollProviderSwitch = ({
  id,
  label,
  checked,
  onCheckedChange,
  disabled = false,
  tooltip,
  assistedBadgeContent
}: {
  id: string,
  label: string,
  checked: boolean,
  onCheckedChange?: (checked: boolean) => Promise<void>,
  disabled?: boolean,
  tooltip?: string,
  assistedBadgeContent?: string
}) => {
  const [isLoading, setIsLoading] = useState(false)

  const handleCheckedChange = async (checked: boolean) => {
    if (!onCheckedChange) return
    setIsLoading(true)
    await onCheckedChange(checked)
    setIsLoading(false)
  }

  const isDisabled = disabled || isLoading || !onCheckedChange

  return (
    <div className='flex items-center gap-x-4 justify-between'>
      <div className='flex items-center gap-x-2'>
        <Label
          htmlFor={`provider-${id}-${label.replace(/\s+/g, '-').toLowerCase()}`}
          className={cn(
            'text-sm font-medium cursor-pointer',
            isDisabled && 'opacity-50'
          )}
        >
          {label}
        </Label>
        {assistedBadgeContent && <AssistedBadge tooltipContent={assistedBadgeContent} />}
        {tooltip && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <InfoIcon className='w-4 h-4 text-gray-500' />
              </TooltipTrigger>
              <TooltipContent>
                <p className='max-w-xs'>{tooltip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      <Switch
        id={`provider-${id}-${label.replace(/\s+/g, '-').toLowerCase()}`}
        checked={checked}
        onCheckedChange={handleCheckedChange}
        className={cn(
          'h-5 w-10',
          isDisabled && 'opacity-50 cursor-not-allowed',
          isLoading && 'data-[state=checked]:bg-gray-400 data-[state=unchecked]:bg-gray-400 animate-pulse cursor-not-allowed'
        )}
        disabled={isDisabled}
      />
    </div>
  )
}

const PayrollProviderConfigItem = (
  { payrollProviderConfig, onUpdate, ...props }:
  {
    payrollProviderConfig: PayrollProviderConfig,
    onUpdate: ({ enabled, promotedInOnboarding, supportsAutomaticDeductions }: { enabled?: boolean, promotedInOnboarding?: boolean, supportsAutomaticDeductions?: boolean }) => Promise<void>
  } & React.ComponentProps<'div'>
) => {
  const [isLoading, setIsLoading] = useState(false)

  const handleUpdate = async ({ enabled, promotedInOnboarding, supportsAutomaticDeductions }: { enabled?: boolean, promotedInOnboarding?: boolean, supportsAutomaticDeductions?: boolean }) => {
    setIsLoading(true)
    await onUpdate({ enabled, promotedInOnboarding, supportsAutomaticDeductions })
    setIsLoading(false)
  }

  const canModifyDeductions = payrollProviderConfig.api_connection_method === 'adp' ||
    (payrollProviderConfig.api_connection_method === 'finch' && payrollProviderConfig.finch_deduction_authentication_methods && payrollProviderConfig.finch_deduction_authentication_methods.length > 0)
  // There is only one assisted method per provider in Finch max, so we can just check that the only assigned auth method is assisted
  // to determine if a provider only supports assisted deductions.
  const isAssistedSetupOnly = payrollProviderConfig.api_connection_method === 'finch' &&
              payrollProviderConfig.finch_authentication_methods?.length === 1 &&
              payrollProviderConfig.finch_authentication_methods[0] === 'assisted'
  const isAssistedDeductionsOnly = payrollProviderConfig.api_connection_method === 'finch' &&
    payrollProviderConfig.finch_deduction_authentication_methods?.length === 1 &&
    payrollProviderConfig.finch_deduction_authentication_methods[0] === 'assisted'

  return (
    <div {...props} className='w-full bg-white border border-gray-300 rounded-lg p-4'>
      {/* Top row with basic info and enabled toggle */}
      <div className='flex items-center justify-between mb-4'>
        <div className='flex items-center gap-2'>
          <img src={payrollProviderConfig.icon_image_url} alt={payrollProviderConfig.name} className='w-10 h-10 rounded-full' />
          <h2 className='text-md font-medium'>
            {payrollProviderConfig.name}
          </h2>
          <div className='flex items-center gap-2'>
            {payrollProviderConfig.api_connection_method === 'finch' && (
              <span className='text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded-md ml-2 cursor-default'>Finch</span>
            )}
            {payrollProviderConfig.api_connection_method === 'merge_dev' && (
              <span className='text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-md ml-2 cursor-default'>Merge</span>
            )}
            {payrollProviderConfig.api_connection_method === 'kombo' && (
              <span className='text-xs text-purple-600 bg-purple-100 px-2 py-1 rounded-md ml-2 cursor-default'>Kombo</span>
            )}
            {payrollProviderConfig.api_connection_method === 'adp' && (
              <span className='text-xs text-red-600 bg-red-100 px-2 py-1 rounded-md ml-2 cursor-default'>Direct</span>
            )}
            {isAssistedSetupOnly && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <span className='text-xs text-yellow-600 bg-yellow-100 px-2 py-1 rounded-md cursor-default flex items-center gap-1'>
                      Assisted
                      <InfoIcon className='w-3 h-3 text-yellow-600' />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className='max-w-xs'>
                      This provider's integration only supports Finch's assisted setup, which requires an involved manual setup,
                      longer setup times, and can be unreliable.
                      Generally, we only use these when critical business needs arise (large opportunities, partnerships, etc.)
                      If you would like to enable this provider, please start a discussion in the #customers Slack channel.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {payrollProviderConfig.shared_connection_name && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <span className='text-xs text-indigo-600 bg-indigo-100 px-2 py-1 rounded-md cursor-default flex items-center gap-1'>
                      Shared connection through {payrollProviderConfig.api_connection_remote_id}
                      <InfoIcon className='w-3 h-3 text-indigo-600' />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className='max-w-xs'>
                      This is a shared payroll connection where multiple employers use the same integration credentials.
                      Employers using this integration require additional setup to ensure their employees are synced to their account.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {isLoading && (
              <LoaderCircleIcon className='w-5 h-5 text-thatch-purple-primary animate-spin inline-block ml-2' />
            )}
          </div>
        </div>
        <PayrollProviderSwitch
          id={payrollProviderConfig.id}
          label='Enabled'
          tooltip='Allow employers to connect this payroll provider'
          checked={payrollProviderConfig.enabled ?? false}
          onCheckedChange={async checked => handleUpdate({ enabled: checked })}
        />
      </div>

      {/* Bottom row with three toggles */}
      <div className='flex items-center justify-between pt-4 border-t border-gray-200'>
        {/* Roster Sync */}
        <div className='flex items-center gap-x-4'>
          <div className='p-3 bg-gray-50 rounded-lg border'>
            <PayrollProviderSwitch
              id={payrollProviderConfig.id}
              label='Employee Sync'
              checked={payrollProviderConfig.supports_automatic_roster_sync ?? false}
              disabled
              tooltip='All providers listed support syncing employees. This cannot be disabled.'
              assistedBadgeContent={
                isAssistedSetupOnly
                  ? `This provider supports syncing employees using Finch's assisted connection, which uses humans to accomplish data syncs.
                    Employee syncs can take up to 2 weeks to start working, and often require additional support burden from us.
                    If you would like to enable this provider, please start a discussion in the #customers Slack channel.
                    Generally, we only use these when critical business needs arise (significant large opportunities, partnerships, etc.)`
                  : undefined
              }
            />
          </div>

          {/* Deductions */}
          <div className='p-3 bg-gray-50 rounded-lg border'>
            <PayrollProviderSwitch
              id={payrollProviderConfig.id}
              label='Automated Deductions'
              checked={payrollProviderConfig.supports_automatic_deductions ?? false}
              disabled={!canModifyDeductions}
              onCheckedChange={canModifyDeductions ? async checked => handleUpdate({ supportsAutomaticDeductions: checked }) : undefined}
              tooltip={
                !canModifyDeductions
                  ? 'This payroll provider does not support automated deductions. Deductions must be handled manually.'
                  : undefined
              }
              assistedBadgeContent={
                isAssistedDeductionsOnly
                  ? `This provider supports automated deductions using Finch's assisted connection, which uses humans to accomplish deduction writing.
                    Assisted deductions can take up to 2 weeks to start working, and often require additional support burden from us.
                    If you would like to enable this provider, please start a discussion in the #customers Slack channel.
                    Generally, we only use these when critical business needs arise (significant large opportunities, partnerships, etc.)`
                  : undefined
              }
            />
          </div>
        </div>
        {/* Promoted in Onboarding */}
        <div className='p-3 bg-gray-50 rounded-lg border border-gray-200'>
          <PayrollProviderSwitch
            id={payrollProviderConfig.id}
            label='Promoted in Onboarding'
            checked={payrollProviderConfig.promoted_in_onboarding ?? false}
            onCheckedChange={async checked => handleUpdate({ promotedInOnboarding: checked })}
            tooltip={`Promoted providers are shown directly to employers during onboarding.
              Non-promoted providers are still available, but employers will need to use the search bar to find them.`}
          />
        </div>
      </div>
    </div>
  )
}

const SortDropdown = (
  { sortColumn, setSortColumn, sortOrder, setSortOrder }:
  {
    sortColumn: 'name' | 'enabled' | 'promoted_in_onboarding' | 'supports_automatic_deductions',
    setSortColumn: (column: 'name' | 'enabled' | 'promoted_in_onboarding' | 'supports_automatic_deductions') => void,
    sortOrder: 'asc' | 'desc',
    setSortOrder: (order: 'asc' | 'desc') => void
  }
) => {
  return (
    <Select value={sortColumn} onValueChange={setSortColumn}>
      <SelectTrigger className='whitespace-nowrap'>
        <SelectValue className='whitespace-nowrap' />
      </SelectTrigger>
      <SelectContent>
        {[
          ['name', 'Name'],
          ['enabled', 'Enabled'],
          ['promoted_in_onboarding', 'Promoted in Onboarding'],
          ['supports_automatic_deductions', 'Supports Automatic Deductions']
        ].map(([value, label]) => (
          <SelectItem
            key={value}
            value={value}
            className='cursor-pointer'
            onPointerDown={() => {
              if (sortColumn === value) {
                setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
              }
            }}
          >
            <div className='flex items-center space-x-4'>
              <span>{label}</span>
              {sortColumn === value && (
                sortOrder === 'desc'
                  ? <ArrowUpNarrowWideIcon className='w-4 h-4 text-gray-500 hover:text-gray-900 transition-all duration-300' />
                  : <ArrowDownNarrowWideIcon className='w-4 h-4 text-gray-500 hover:text-gray-900 transition-all duration-300' />
              )}
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function AdminPayrollProviderConfigListImpl () {
  const { reload } = useWindowLocation()

  const [searchQuery, setSearchQuery] = useState('')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [sortColumn, setSortColumn] = useState<'name' | 'enabled' | 'promoted_in_onboarding' | 'supports_automatic_deductions'>('name')
  const [showFinchAssisted, setShowFinchAssisted] = useState(false)

  const { data: payrollProviderConfigs = [], isLoading, error } = useGetPayrollProviderConfigs()
  const updatePayrollProviderConfigMutation = useUpdatePayrollProviderConfig()
  const syncPayrollProviderConfigsMutation = useSyncPayrollProviderConfigs()
  const queryClient = useQueryClient()

  const filteredProviders = payrollProviderConfigs.filter((payrollProviderConfig) => {
    const matchesSearch = payrollProviderConfig.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      payrollProviderConfig.api_connection_remote_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      payrollProviderConfig.api_connection_remote_id.toLowerCase().replaceAll('_', ' ').includes(searchQuery.toLowerCase()) ||
      payrollProviderConfig.api_connection_remote_id.toLowerCase().replaceAll('_', '').includes(searchQuery.toLowerCase())

    if (!matchesSearch) return false

    // If showFinchAssisted is false, hide Finch configs that only have "assisted" authentication
    if (!showFinchAssisted &&
        payrollProviderConfig.api_connection_method === 'finch' &&
        payrollProviderConfig.finch_authentication_methods?.length === 1 &&
        payrollProviderConfig.finch_authentication_methods[0] === 'assisted') {
      return false
    }

    return true
  })

  const filteredAndSortedPayrollProviderConfigs = filteredProviders.sort((a, b) => {
    if (sortColumn === 'name') {
      return sortOrder === 'desc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name)
    } else {
      // For boolean fields, desc should show true values first (enabled, promoted, etc.)
      return sortOrder === 'desc' ? (a[sortColumn] === b[sortColumn] ? 0 : a[sortColumn] ? -1 : 1) : (a[sortColumn] === b[sortColumn] ? 0 : a[sortColumn] ? 1 : -1)
    }
  })

  const handlePayrollProviderConfigUpdate = async (payrollProviderConfig: PayrollProviderConfig, {
    enabled,
    promotedInOnboarding,
    supportsAutomaticDeductions
  }: { enabled?: PayrollProviderConfig['enabled'], promotedInOnboarding?: PayrollProviderConfig['promoted_in_onboarding'], supportsAutomaticDeductions?: PayrollProviderConfig['supports_automatic_deductions'] }) => {
    return new Promise<void>((resolve) => {
      updatePayrollProviderConfigMutation.mutate(
        { id: payrollProviderConfig.id, enabled, promoted_in_onboarding: promotedInOnboarding, supports_automatic_deductions: supportsAutomaticDeductions },
        {
          onSuccess: (updatedPayrollProviderConfig) => {
            // Update only the specific updated payroll provider config in the cache
            queryClient.setQueryData(
              getPayrollProviderConfigsQueryKey(),
              (oldData: PayrollProviderConfig[] | undefined) =>
                oldData?.map(config =>
                  config.id === updatedPayrollProviderConfig.id
                    ? updatedPayrollProviderConfig
                    : config
                ) || []
            )

            if (enabled !== undefined) {
              toast.success(`${payrollProviderConfig.name} is now ${enabled ? 'enabled' : 'disabled'}`)
            } else if (promotedInOnboarding !== undefined) {
              toast.success(`${payrollProviderConfig.name} ${promotedInOnboarding ? 'is now' : 'is no longer'} promoted in onboarding`)
            } else if (supportsAutomaticDeductions !== undefined) {
              toast.success(`${payrollProviderConfig.name} ${supportsAutomaticDeductions ? 'now supports' : 'no longer supports'} automatic deductions`)
            }
            resolve(updatedPayrollProviderConfig)
          },
          onError: (response) => {
            toast.error(`Failed to update payroll provider: ${response.message}`)
            resolve()
          },
        })
    })
  }

  const handleSyncPayrollProviderConfigs = () => {
    syncPayrollProviderConfigsMutation.mutate()
  }

  const promotedPayrollProviderConfigs = payrollProviderConfigs.filter(payrollProviderConfig => payrollProviderConfig.promoted_in_onboarding && payrollProviderConfig.enabled)

  let gridClass
  if ((promotedPayrollProviderConfigs.length + 1) % 3 === 0) {
    gridClass = 'grid-cols-3'
  } else if ((promotedPayrollProviderConfigs.length + 1) % 5 === 0) {
    gridClass = 'grid-cols-5'
  } else {
    gridClass = 'grid-cols-4'
  }

  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <LoaderCircleIcon className='w-10 h-10 text-thatch-purple-primary animate-spin' />
        <span className='ml-2 text-lg'>Loading payroll providers...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className='flex flex-col items-center justify-center h-64'>
        <div className='text-red-500 text-lg mb-4'>Failed to load payroll providers</div>
        <div className='text-sm text-gray-500 mb-4'>{error.message}</div>
        <Button onClick={() => reload()}>Reload Page</Button>
      </div>
    )
  }

  return (
    <>
      <div className='grid grid-cols-4 justify-between'>
        <div className='col-span-3'>
          <h1 className='text-2xl font-bold'>Payroll Providers</h1>
          <h2 className='text-sm'>This list determines which payroll providers are made available to employers during the onboarding process.</h2>
        </div>
        <div className='text-right'>
          <Button
            variant='outline'
            onClick={handleSyncPayrollProviderConfigs}
            disabled={syncPayrollProviderConfigsMutation.isPending}
          >
            <RefreshCcw className={`w-4 h-4 mr-2 ${syncPayrollProviderConfigsMutation.isPending ? 'animate-spin' : ''}`} />
            Re-sync payroll providers
          </Button>
        </div>
      </div>
      <div className='mt-4'>
        <CollapsibleContainer
          heading='View promoted payroll providers in employer onboarding'
          defaultOpen={false}
        >
          <div className='bg-white p-4 rounded-lg'>
            <h1 className='h1 mb-4' id='welcome'>
              Connect and sync your employee directory
            </h1>
            <h2 className='text-sm text-gray-700 font-md text-left mb-12'>
              By connecting your payroll, you can easily invite employees to Thatch and manage payroll deductions for any out-of-pocket costs.
            </h2>
            <Input
              placeholder='Search for your payroll provider'
              className='border border-gray-300'
              disabled
            />
            <div className='flex flex-col gap-4 mt-6'>
              <div className={`grid ${gridClass} gap-2 md:gap-6`}>
                {promotedPayrollProviderConfigs.map(payrollProviderConfig => (
                  <div
                    key={payrollProviderConfig.id}
                    className='border border-gray-300 shadow-md rounded-lg p-4 hover:shadow-lg hover:cursor-not-allowed hover:border-thatch-purple transition-all duration-300'
                  >
                    <div
                      className='flex items-center justify-center h-12 bg-no-repeat bg-contain bg-center'
                      style={{ backgroundImage: `url(${payrollProviderConfig.logo_image_url})` }}
                    />
                  </div>
                ))}
                <div className='border border-gray-300 shadow-md rounded-lg p-4 hover:shadow-lg hover:cursor-not-allowed hover:border-thatch-purple transition-all duration-300'>
                  <div className='flex items-center justify-center h-full'>
                    Other
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CollapsibleContainer>
      </div>
      <div className='flex flex-col h-full mt-4'>
        <div className='flex items-center space-x-4 mb-4'>
          <div className='relative flex-grow'>
            <Input
              placeholder='Search'
              className='text-sm border-gray-300'
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
            />
            <div className='absolute right-2 top-0 bottom-0 h-full flex items-center justify-center'>
              {searchQuery.length > 0 && (
                <Button
                  variant='ghost'
                  className='rounded-full w-8 h-8 p-0 flex items-center justify-center'
                  onClick={() => setSearchQuery('')}
                >
                  <X className='w-4 h-4 text-gray-500' />
                </Button>
              )}
            </div>
          </div>
          <div className='flex-shrink-0'>
            <SortDropdown
              sortColumn={sortColumn}
              setSortColumn={setSortColumn}
              sortOrder={sortOrder}
              setSortOrder={setSortOrder}
            />
          </div>
        </div>
        <div className='flex items-center gap-2 mb-4'>
          <Switch
            id='show-finch-assisted'
            checked={showFinchAssisted}
            onCheckedChange={setShowFinchAssisted}
            className='h-5 w-10'
          />
          <Label htmlFor='show-finch-assisted' className='cursor-pointer'>
            Show Finch assisted
          </Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <InfoIcon className='w-4 h-4 text-gray-500' />
              </TooltipTrigger>
              <TooltipContent>
                <p className='max-w-xs'>Finch assisted connections only refresh weekly, and require significantly more work to set up. We do not support these unless a significant business case is made.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className='mt-4 flex flex-col gap-4 overflow-y-auto flex-grow max-h-[60vh]'>
          {filteredAndSortedPayrollProviderConfigs.length === 0 && (
            <div className='text-center text-gray-500'>
              No payroll providers found matching your search.
              <br />
              Try enabling "Show Finch assisted" to see all providers.
              <br />
              If your provider is not listed on this page, it is currently not supported by Thatch.
              <br />
              If you have an important use case for a provider that is not listed here, please reach out in the #customers Slack channel.
            </div>
          )}
          {filteredAndSortedPayrollProviderConfigs.map((payrollProviderConfig) => (
            <PayrollProviderConfigItem
              key={payrollProviderConfig.id}
              payrollProviderConfig={payrollProviderConfig}
              onUpdate={updates => handlePayrollProviderConfigUpdate(payrollProviderConfig, updates)}
            />
          ))}
        </div>
      </div>
    </>
  )
}

export function AdminPayrollProviderConfigList () {
  return <AdminPayrollProviderConfigListImpl />
}
